import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  siteId?: string;
  siteName?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  activeSiteId: string | null;
  activeSiteName: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  setActiveSite: (siteId: string | null, siteName: string | null) => void;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role?: string;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'REGISTER_FAILURE' }
  | { type: 'SET_ACTIVE_SITE'; payload: { siteId: string | null; siteName: string | null } };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isLoading: Boolean(localStorage.getItem('token')),
  isAuthenticated: Boolean(localStorage.getItem('token')),
  activeSiteId: localStorage.getItem('activeSiteId'),
  activeSiteName: localStorage.getItem('activeSiteName'),
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
        // For regular users, activeSiteId is always their own siteId
        activeSiteId: action.payload.user.role === 'super_admin'
          ? (state.activeSiteId || null)
          : (action.payload.user.siteId || null),
        activeSiteName: action.payload.user.role === 'super_admin'
          ? (state.activeSiteName || null)
          : (action.payload.user.siteName || null),
      };
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return { ...state, isLoading: false };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
        activeSiteId: null,
        activeSiteName: null,
      };
    case 'SET_ACTIVE_SITE':
      return {
        ...state,
        activeSiteId: action.payload.siteId,
        activeSiteName: action.payload.siteName,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const hydrate = async () => {
      if (!state.token || state.user) return;
      
      // Add a small delay to prevent rapid calls during app initialization
      await new Promise(resolve => setTimeout(resolve, 100));
      
      try {
        console.log('Validating token and loading user profile...');
        dispatch({ type: 'LOGIN_START' });
        const res = await fetch('/api/auth/profile', {
          headers: {
            Authorization: `Bearer ${state.token}`,
          },
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || 'Failed to load profile');
        }
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: data.user, token: state.token },
        });
      } catch (error) {
        console.error('Token validation failed:', error);
        dispatch({ type: 'LOGOUT' });
      }
    };

    hydrate();
  }, [state.token, state.user]);

  useEffect(() => {
    if (state.token) {
      localStorage.setItem('token', state.token);
    } else {
      localStorage.removeItem('token');
    }
  }, [state.token]);

  // Persist activeSiteId/Name to localStorage
  useEffect(() => {
    if (state.activeSiteId) {
      localStorage.setItem('activeSiteId', state.activeSiteId);
    } else {
      localStorage.removeItem('activeSiteId');
    }
    if (state.activeSiteName) {
      localStorage.setItem('activeSiteName', state.activeSiteName);
    } else {
      localStorage.removeItem('activeSiteName');
    }
  }, [state.activeSiteId, state.activeSiteName]);

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'LOGIN_START' });

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: data.user, token: data.token },
      });

      toast.success('Login successful!');
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      toast.error(error instanceof Error ? error.message : 'Login failed');
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      dispatch({ type: 'REGISTER_START' });

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      dispatch({
        type: 'REGISTER_SUCCESS',
        payload: { user: data.user, token: data.token },
      });

      toast.success('Registration successful!');
    } catch (error) {
      dispatch({ type: 'REGISTER_FAILURE' });
      toast.error(error instanceof Error ? error.message : 'Registration failed');
    }
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  const setActiveSite = useCallback((siteId: string | null, siteName: string | null) => {
    dispatch({ type: 'SET_ACTIVE_SITE', payload: { siteId, siteName } });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        setActiveSite,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
